import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Users, Search, RefreshCw, Phone, Mail, MapPin,
  MessageSquare, ChevronRight, FileText, ShoppingBag, User
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { formatDate, formatDateTime } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PROFIL_LABELS = { particulier: 'Particulier', entreprise: 'Entreprise', association: 'Association', ce: 'CE', groupe: 'Partenaire' };
const PROFIL_COLORS = { particulier: '#9CA3AF', entreprise: '#5DBCBF', association: '#7DB1FF', ce: '#C58B19', groupe: '#D4AF37' };
const STATUTS_CLIENT = { actif: '#1F9D7A', prospect: '#7DB1FF', vip: '#D4AF37', inactif: '#9CA3AF', bloque: '#e53e3e' };
const STATUTS_DEVIS = { brouillon: '#9CA3AF', envoye: '#5DBCBF', signe: '#1F9D7A', acompte_paye: '#D4AF37', solde_paye: '#D4AF37', annule: '#e53e3e', commande: '#8B5CF6' };

function FicheClient({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [activeTab, setActiveTab] = useState('devis');
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadFiche = useCallback(() => {
    fetch(`${BACKEND}/api/auth/crm/clients/${userId}/fiche`, { headers: H })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  // eslint-disable-next-line
  }, [userId]);

  useEffect(() => { loadFiche(); }, [loadFiche]);

  async function addNote() {
    if (!note.trim()) return;
    await fetch(`${BACKEND}/api/auth/crm/clients/${userId}/note`, {
      method: 'POST', headers: H, body: JSON.stringify({ texte: note })
    });
    toast.success('Note ajoutée'); setNote(''); loadFiche();
  }

  async function updateStatut(statut) {
    await fetch(`${BACKEND}/api/auth/crm/clients/${userId}/statut`, {
      method: 'PUT', headers: H, body: JSON.stringify({ statut })
    });
    toast.success('Statut mis à jour'); loadFiche();
  }

  if (loading) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.7)' }}>
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Clients</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>CRM</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Suivi, notes et opportunites</p>
      </div>

      <div className="w-8 h-8 rounded-full animate-spin" style={{ border: '2px solid rgba(255,255,255,0.1)', borderTopColor: '#D4AF37' }} />
    </div>
  );
  if (!data) return null;

  const stats = data.stats || {};
  const pc = PROFIL_COLORS[data.profile] || '#9CA3AF';
  const TABS = [
    { id: 'devis', label: `Devis (${(data.devis||[]).length})`, icon: <FileText size={12}/> },
    { id: 'commandes', label: `Boutique (${(data.commandes||[]).length})`, icon: <ShoppingBag size={12}/> },
    { id: 'notes', label: `Notes (${(data.notes_crm||[]).length})`, icon: <MessageSquare size={12}/> },
    { id: 'infos', label: 'Infos', icon: <User size={12}/> },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.75)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="w-full max-w-3xl rounded-2xl" style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)' }}>
          {/* Header */}
          <div className="p-5" style={{ borderBottom: '1px solid var(--admin-border-soft)' }}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold"
                  style={{ background: `${pc}15`, color: pc }}>
                  {(data.prenom || data.nom || data.email || 'U')[0].toUpperCase()}
                </div>
                <div>
                  <p className="font-bold text-lg" style={{ color: 'var(--admin-text)' }}>
                    {data.prenom && data.nom ? `${data.prenom} ${data.nom}` : (data.nom || data.email)}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: `${pc}15`, color: pc }}>{PROFIL_LABELS[data.profile] || 'Particulier'}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: `${STATUTS_CLIENT[data.statut_client] || '#9CA3AF'}15`, color: STATUTS_CLIENT[data.statut_client] || '#9CA3AF' }}>{data.statut_client || 'actif'}</span>
                  </div>
                </div>
              </div>
              <button onClick={onClose} style={{ color: 'var(--admin-muted)', fontSize: 20 }}>✕</button>
            </div>
            {/* KPIs */}
            <div className="grid grid-cols-4 gap-3 mt-4">
              {[
                { label: 'CA Total', value: `${stats.ca_total?.toFixed(0)||0}€`, color: 'var(--admin-gold)' },
                { label: 'Devis', value: stats.nb_devis||0, color: 'var(--admin-teal)' },
                { label: 'Commandes', value: stats.nb_devis_payes||0, color: 'var(--admin-green)' },
                { label: 'Cashback', value: `${data.fidelite?.cashback_disponible?.toFixed(2)||'0.00'}€`, color: 'var(--admin-amber)' },
              ].map((k,i) => (
                <div key={i} className="p-2 rounded-xl text-center" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <p className="text-base font-bold" style={{ color: k.color }}>{k.value}</p>
                  <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{k.label}</p>
                </div>
              ))}
            </div>
            {/* Contacts */}
            <div className="flex flex-wrap gap-3 mt-3">
              {data.email && <a href={`mailto:${data.email}`} className="flex items-center gap-1 text-xs" style={{ color: 'var(--admin-teal)' }}><Mail size={11}/>{data.email}</a>}
              {data.telephone && <a href={`tel:${data.telephone}`} className="flex items-center gap-1 text-xs" style={{ color: 'var(--admin-teal)' }}><Phone size={11}/>{data.telephone}</a>}
              {data.adresse_personnelle && <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--admin-muted)' }}><MapPin size={11}/>{data.adresse_personnelle}</span>}
            </div>
            {/* Statut client */}
            <div className="flex gap-2 mt-3 flex-wrap">
              {Object.entries(STATUTS_CLIENT).map(([k, color]) => (
                <button key={k} onClick={() => updateStatut(k)}
                  className="text-xs px-2.5 py-1 rounded-full font-semibold"
                  style={{ background: data.statut_client===k ? `${color}20` : 'rgba(255,255,255,0.04)', color: data.statut_client===k ? color : 'rgba(255,255,255,0.3)', border: `1px solid ${data.statut_client===k ? color+'40' : 'rgba(255,255,255,0.08)'}` }}>
                  {k}
                </button>
              ))}
            </div>
          </div>
          {/* Tabs */}
          <div className="flex gap-0.5 p-2" style={{ borderBottom: '1px solid var(--admin-border-soft)' }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium flex-1 justify-center"
                style={activeTab===tab.id ? { background: 'var(--admin-teal-dim)', color: 'var(--admin-teal)' } : { color: 'var(--admin-muted)' }}>
                {tab.icon}{tab.label}
              </button>
            ))}
          </div>
          <div className="p-5">
            {activeTab === 'devis' && (
              <div className="space-y-2">
                {(data.devis||[]).length === 0 ? <p className="text-center py-8 text-sm" style={{ color: 'var(--admin-faint)' }}>Aucun devis</p>
                  : (data.devis||[]).map(q => {
                    const sc = STATUTS_DEVIS[q.statut] || '#9CA3AF';
                    return (
                      <div key={q.id} className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'var(--admin-surface)', border: `1px solid ${sc}15` }}>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-bold" style={{ color: 'var(--admin-gold)' }}>{q.numero}</span>
                            <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: `${sc}15`, color: sc }}>{q.statut}</span>
                          </div>
                          <p className="text-xs mt-0.5" style={{ color: 'var(--admin-faint)' }}>{formatDate(q.created_at)} · {(q.items||[]).length} articles</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-sm" style={{ color: 'var(--admin-gold)' }}>{q.total?.toFixed(0)||0}€</p>
                          <a href={`/devis/${q.id}`} target="_blank" rel="noreferrer" className="text-xs" style={{ color: 'var(--admin-teal)' }}>Voir →</a>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
            {activeTab === 'commandes' && (
              <div className="space-y-2">
                {(data.commandes||[]).length === 0 ? <p className="text-center py-8 text-sm" style={{ color: 'var(--admin-faint)' }}>Aucune commande boutique</p>
                  : (data.commandes||[]).map(o => (
                    <div key={o.id} className="flex justify-between p-3 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                      <div>
                        <p className="font-mono text-xs font-bold" style={{ color: 'var(--admin-gold)' }}>{o.numero}</p>
                        <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{formatDate(o.created_at)} · {o.statut}</p>
                      </div>
                      <p className="font-bold text-sm" style={{ color: 'var(--admin-gold)' }}>{o.total?.toFixed(0)||0}€</p>
                    </div>
                  ))}
              </div>
            )}
            {activeTab === 'notes' && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Ajouter une note..."
                    style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)', flex: 1 }}
                    onKeyDown={e => { if (e.key === 'Enter') addNote(); }} />
                  <Button onClick={addNote} size="sm" style={{ background: 'rgba(125,177,255,0.15)', color: 'var(--admin-blue)' }}><MessageSquare size={14}/></Button>
                </div>
                {[...((data.notes_crm||[]))].reverse().map((n,i) => (
                  <div key={i} className="p-3 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid rgba(125,177,255,0.1)' }}>
                    <p className="text-sm" style={{ color: 'var(--admin-text)' }}>{n.texte}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--admin-faint)' }}>{n.auteur} · {formatDateTime(n.date)}</p>
                  </div>
                ))}
                {(data.notes_crm||[]).length === 0 && <p className="text-center py-6 text-sm" style={{ color: 'var(--admin-faint)' }}>Aucune note</p>}
              </div>
            )}
            {activeTab === 'infos' && (
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { label: 'Email', value: data.email },
                  { label: 'Téléphone', value: data.telephone || '—' },
                  { label: 'Adresse', value: data.adresse_personnelle || '—' },
                  { label: 'Profil', value: PROFIL_LABELS[data.profile] || 'Particulier' },
                  { label: 'Membre depuis', value: data.created_at ? formatDate(data.created_at) : '—' },
                  { label: 'Panier moyen', value: `${stats.panier_moyen?.toFixed(0)||0}€` },
                  { label: 'CA Locations', value: `${stats.ca_locations?.toFixed(2)||0}€` },
                  { label: 'CA Boutique', value: `${stats.ca_boutique?.toFixed(2)||0}€` },
                  { label: 'Palier fidélité', value: data.fidelite?.palier_actuel || 'Nouveau client' },
                  { label: 'Cashback dispo', value: `${data.fidelite?.cashback_disponible?.toFixed(2)||'0.00'}€` },
                ].map((r,i) => (
                  <div key={i} className="p-3 rounded-xl" style={{ background: 'var(--admin-surface)' }}>
                    <p className="text-xs mb-0.5" style={{ color: 'var(--admin-muted)' }}>{r.label}</p>
                    <p className="text-sm font-semibold" style={{ color: 'var(--admin-text)' }}>{r.value}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminCRM() {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [profileFilter, setProfileFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BACKEND}/api/auth/crm/clients?limit=50`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (profileFilter) url += `&profile=${profileFilter}`;
      const d = await fetch(url, { headers: H }).then(r => r.json());
      setClients(d.clients || []); setTotal(d.total || 0);
    } catch { toast.error('Erreur'); }
    setLoading(false);
  // eslint-disable-next-line
  }, [search, profileFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }} data-testid="admin-crm">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="adm-title"  style={{ color: 'var(--admin-gold)' }}>CRM Clients</h1>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>{total} clients · Fiches complètes</p>
        </div>
        <Button onClick={load} variant="outline" size="sm" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}><RefreshCw size={14} className="mr-1"/>Actualiser</Button>
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--admin-faint)' }} />
          <Input placeholder="Nom, email, téléphone..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9"
            style={{ background: 'var(--admin-surface)', borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }} />
        </div>
        <select value={profileFilter} onChange={e => setProfileFilter(e.target.value)} className="px-3 py-2 rounded-xl text-sm"
          style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)' }}>
          <option value="">Tous profils</option>
          {Object.entries(PROFIL_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="space-y-2">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-xl" style={{ background: 'var(--admin-surface)' }} />)}</div>
      ) : clients.length === 0 ? (
        <div className="text-center py-16" style={{ color: 'var(--admin-faint)' }}><Users size={40} className="mx-auto mb-3 opacity-20" /><p>Aucun client trouvé</p></div>
      ) : (
        <div className="space-y-2">
          {clients.map(c => {
            const pc = PROFIL_COLORS[c.profile] || '#9CA3AF';
            const sc = STATUTS_CLIENT[c.statut_client] || '#9CA3AF';
            return (
              <div key={c.id} className="flex items-center gap-4 p-4 rounded-xl cursor-pointer hover:opacity-90"
                style={{ background: 'var(--admin-surface)', border: `1px solid ${pc}15` }} onClick={() => setSelectedId(c.id)}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold flex-shrink-0" style={{ background: `${pc}15`, color: pc }}>
                  {(c.prenom || c.nom || c.email || 'U')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm truncate" style={{ color: 'var(--admin-text)' }}>{c.prenom && c.nom ? `${c.prenom} ${c.nom}` : (c.nom || c.email)}</p>
                    <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: `${pc}15`, color: pc, fontSize: 10 }}>{PROFIL_LABELS[c.profile] || 'Part.'}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: `${sc}10`, color: sc, fontSize: 10 }}>{c.statut_client || 'actif'}</span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{c.email}{c.telephone && ` · ${c.telephone}`}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-sm" style={{ color: 'var(--admin-gold)' }}>{c.stats?.ca_total?.toFixed(0)||0}€</p>
                  <p className="text-xs" style={{ color: 'var(--admin-faint)' }}>{c.stats?.nb_devis||0} devis</p>
                </div>
                <ChevronRight size={14} style={{ color: 'var(--admin-faint)', flexShrink: 0 }} />
              </div>
            );
          })}
        </div>
      )}
      {selectedId && <FicheClient userId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}
